import React from 'react'
import { l } from '@manychat/manyui'

import { PRICING_LINK } from 'common/billing/constants'
import { ProductSlug } from 'common/billing/constants/ProductSlug'
import { getCurrency } from 'common/billing/entities/pro/utils/convertPrice'
import { WebProductSlug } from 'shared/api/requests/billing/schemas'
import { MigrationConsentPlanOffer } from 'shared/api/requests/billingFlex/schemas'

import { FreePlanCard } from '../../Paywall/variants/UniversalPaywall/steps/Plan/FreePlanCard'
import { PaidPlanCard } from '../../Paywall/variants/UniversalPaywall/steps/Plan/PaidPlanCard'
import { PeriodToggle } from '../../Paywall/variants/UniversalPaywall/steps/Plan/PeriodToggle'
import { usePlansViewContext } from '../../Paywall/variants/UniversalPaywall/steps/Plan/PlansViewContext'
import { PricingLink } from '../../Paywall/variants/UniversalPaywall/steps/Plan/PricingLink'

import cm from './AllPlansView.module.css'

/**
 * Customized version of the DefaultPlansView component for the ForcedMigration flow.
 */
export const AllPlansView = ({
  registerConsent,
  targetSlug,
}: {
  registerConsent: (slug: ProductSlug, shownPlans: MigrationConsentPlanOffer[]) => void
  targetSlug: ProductSlug
}) => {
  const { freePlanCard, paidPlanCards } = usePlansViewContext()
  const currency = getCurrency()

  const shownPlans: MigrationConsentPlanOffer[] = paidPlanCards.map((card) => ({
    plan: card.slug as WebProductSlug,
    price: {
      amount: card.price.amount,
      currency: currency,
    },
  }))

  const customPaidPlanCards = paidPlanCards.map((card) => {
    if (card.slug === targetSlug) {
      return {
        ...card,
        isPopular: true,
        badge: l.translate('Best Fit'),
        onConsent: () => registerConsent(card.slug, shownPlans),
        ctaProps: { ...card.ctaProps, variant: 'primary' as const, className: 'bg-accent' },
      }
    }
    return {
      ...card,
      isPopular: false,
      badge: undefined,
      onConsent: () => registerConsent(card.slug, shownPlans),
      ctaProps: { ...card.ctaProps, className: '', variant: 'default' as const },
    }
  })

  return (
    <div className={cm.layout} data-test-id="plan-select">
      <header className="d-flex flex-col gap-xs align-center">
        <h1 className="d-block text-center">
          {l.translate('1M+ Creators love Manychat. Now it’s your turn.')}
        </h1>
        <PeriodToggle className="d-block" />
      </header>
      <section className={cm.cardsRow} aria-label={l.translate('Available plans')}>
        <ul className={cm.cardsList}>
          {freePlanCard && <FreePlanCard {...freePlanCard} />}
          {customPaidPlanCards.map((card) => (
            <PaidPlanCard key={card.title} {...card} />
          ))}
        </ul>
      </section>
      <footer className="text-body-sm">
        <span>{l.translate('Need a deep dive?')}</span>{' '}
        <PricingLink href={PRICING_LINK} target="_blank" variant="small" className="d-inline-flex">
          {l.translate('View plan comparison table')}
        </PricingLink>
      </footer>
    </div>
  )
}
